import ChevronRight from '@mui/icons-material/ChevronRight';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import PropTypes from 'prop-types';
import * as React from 'react';
import { useSelector } from 'react-redux';
import {
  useLazyGetApiAttachmentsQuery,
  useLazyGetApiCtpsQuery,
  useLazyGetApiDeliverablesQuery,
  useLazyGetApiPnrsQuery,
  useLazyGetApiRipsQuery,
  useLazyGetApiTarsQuery,
  useLazyGetApiTripReportsQuery,
} from '../../../app/athenaApiGen';
import { isFormTypeAvailable } from '../../utilities';

// Rotating tips shown under the progress bar during the prep cascade. Mix of
// "here's what's happening" status + "here's what you can do" guidance so
// the user has something to read while we walk every deliverable / form.
// Order isn't load-bearing; tweak freely.
const PREP_TIPS = [
  'Walking through every deliverable to find attached files…',
  'Tip — you can deselect specific files in the next step before downloading.',
  'Checking each form (TAR, TR, PNR, CTP, RIP) for attached receipts and docs…',
  'Tip — click Cancel anytime to stop.',
  'Pulling together everything attached to your project, its deliverables, and its forms…',
  'Tip — the next step shows you exactly what’s included before anything downloads.',
];

// Run an async mapper over `items` with at most `limit` concurrent calls in
// flight. Existed as a stopgap for the prepare-download cascade: the BE has
// no aggregated manifest endpoint yet, so the FE has to issue one attachment
// fetch per deliverable / form entity. Doing them strictly serially makes
// prep take ~40s on a 200-deliverable project; parallelising with a cap of
// ~6 cuts that to a few seconds without hammering the API. Replace with a
// single BE manifest call when that ships.
async function mapWithConcurrency(items, limit, mapper, isAborted) {
  const results = new Array(items.length);
  let cursor = 0;
  const worker = async () => {
    while (cursor < items.length) {
      if (isAborted?.()) return;
      const idx = cursor;
      cursor += 1;
      try {
        results[idx] = await mapper(items[idx], idx);
      } catch {
        results[idx] = undefined;
      }
    }
  };
  const workerCount = Math.max(1, Math.min(limit, items.length));
  const workers = [];
  for (let i = 0; i < workerCount; i += 1) workers.push(worker());
  await Promise.all(workers);
  return results;
}

/**
 * ProjectDownloader - Option 2: Multi-Fetch Attachment API Approach
 *
 * This component fetches attachments by querying multiple endpoints:
 * 1. Project-level attachments (objectType='Project')
 * 2. Deliverable attachments for both project and program deliverables (objectType='Deliverable')
 * 3. Form attachments (TAR, TR, PNR, CTP, RIP) - expense receipts and other direct attachments
 *
 * The review dialog shows a complete breakdown of all project objects to give users
 * visibility into what's included in the download.
 */
export default function ProjectDownloader({ open, onClose, project }) {
  const loginToken = useSelector((state) => state.user.loginToken);
  const availableForms = useSelector((state) => state.user.availableForms);
  const [downloadReviewOpen, setDownloadReviewOpen] = React.useState(false);
  const [downloadData, setDownloadData] = React.useState(null);
  const [downloadDialogOpen, setDownloadDialogOpen] = React.useState(false);
  const [downloadProgress, setDownloadProgress] = React.useState({
    current: 0,
    total: 0,
    message: '',
  });
  // Phase drives the progress dialog title (Preparing vs Downloading) so the
  // shared dialog doesn't mislabel itself during the prep cascade.
  const [downloadPhase, setDownloadPhase] = React.useState('idle');
  // Latches true once we discover the project is large during prep so the
  // user sees a "this'll take a moment" hint even when the determinate progress
  // total resets between phases. Reset alongside other dialog state on close.
  const [isLargeProject, setIsLargeProject] = React.useState(false);
  // Abort signal for prep / download. A ref (not state) so concurrent
  // closures inside the parallel workers and the download loops always read
  // the latest value without a re-render dependency. Set to true from the
  // Cancel button; cleared at the start of each new prep/download.
  const cancelRef = React.useRef(false);
  // Timestamp captured when the download phase begins. Drives the rolling
  // "X seconds remaining" estimate in the progress dialog. Stored as a ref
  // since the value only matters at render time and never needs to trigger
  // a re-render itself (re-renders come from setDownloadProgress).
  const downloadStartRef = React.useRef(0);

  // Index into PREP_TIPS — driven by a setInterval that runs only while we're
  // in the prep phase, so the tip line cycles while the user waits and stops
  // (without churn) once we hand off to the review or download stages.
  const [tipIndex, setTipIndex] = React.useState(0);
  React.useEffect(() => {
    if (downloadPhase !== 'preparing') return undefined;
    setTipIndex(0);
    const timer = setInterval(() => {
      setTipIndex((i) => (i + 1) % PREP_TIPS.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [downloadPhase]);
  const [noAttachmentsDialogOpen, setNoAttachmentsDialogOpen] =
    React.useState(false);
  const [downloadErrorDialogOpen, setDownloadErrorDialogOpen] =
    React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [downloadSummary, setDownloadSummary] = React.useState(null);

  // Per-attachment selection for the review dialog. Defaults to "all selected"
  // (matches the legacy one-click "Download all" behavior) and rebuilds when
  // a new downloadData arrives. Selection is keyed on attachmentId since
  // that's what we hit at download time.
  const [selectedIds, setSelectedIds] = React.useState(() => new Set());

  React.useEffect(() => {
    if (!downloadData) return;
    const ids = new Set();
    for (const a of downloadData.projectAttachments || []) {
      if (a?.attachmentId) ids.add(a.attachmentId);
    }
    for (const { attachments } of
      downloadData.deliverableAttachmentsMap?.values() || []) {
      for (const a of attachments) if (a?.attachmentId) ids.add(a.attachmentId);
    }
    for (const map of [
      downloadData.tarAttachmentsMap,
      downloadData.trAttachmentsMap,
      downloadData.pnrAttachmentsMap,
      downloadData.ctpAttachmentsMap,
      downloadData.ripAttachmentsMap,
    ]) {
      for (const { attachments } of map?.values() || []) {
        for (const a of attachments) if (a?.attachmentId) ids.add(a.attachmentId);
      }
    }
    setSelectedIds(ids);
  }, [downloadData]);

  // Toggle a single attachment's selection.
  const toggleId = (id) => {
    if (!id) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Bulk set selection for a list of ids — used by tri-state section /
  // master checkboxes when the user clicks them.
  const setManySelected = (ids, value) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (!id) continue;
        if (value) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  };

  // Tri-state derivation: 'none' (unchecked), 'all' (checked), 'some'
  // (indeterminate). Used by the master + per-section header checkboxes.
  const triState = (ids) => {
    if (!ids.length) return 'none';
    let hit = 0;
    for (const id of ids) if (selectedIds.has(id)) hit += 1;
    if (hit === 0) return 'none';
    if (hit === ids.length) return 'all';
    return 'some';
  };

  // Expansion state for the section accordions. Default = empty Set (all
  // collapsed) — most users just hit "Download all" without ever drilling
  // in, so we start tidy. Reset alongside the selection when downloadData
  // changes so re-opening the dialog doesn't carry stale UI state.
  const [expandedSections, setExpandedSections] = React.useState(
    () => new Set(),
  );
  React.useEffect(() => {
    setExpandedSections(new Set());
  }, [downloadData]);
  const isExpanded = (key) => expandedSections.has(key);
  const toggleExpanded = (key) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const [fetchDeliverables] = useLazyGetApiDeliverablesQuery();
  const [fetchAttachments] = useLazyGetApiAttachmentsQuery();
  const [fetchTars] = useLazyGetApiTarsQuery();
  const [fetchTripReports] = useLazyGetApiTripReportsQuery();
  const [fetchPnrs] = useLazyGetApiPnrsQuery();
  const [fetchCtps] = useLazyGetApiCtpsQuery();
  const [fetchRips] = useLazyGetApiRipsQuery();

  // Auto-start download when dialog opens
  React.useEffect(() => {
    if (open && project) {
      handlePrepareDownload();
    }
  }, [open, project]);

  // User-initiated cancel for prep or download. Flips the ref so async loops
  // bail at their next iteration boundary; in-flight fetches finish but their
  // results get discarded. Then closes the dialog as if the user dismissed it.
  const handleCancelDownload = () => {
    cancelRef.current = true;
    handleClose();
  };

  // Component-scoped wrapper around mapWithConcurrency that auto-wires the
  // cancel ref so every prep / download loop honours the Cancel button
  // without each call site repeating the predicate.
  const runConcurrent = (items, limit, mapper) =>
    mapWithConcurrency(items, limit, mapper, () => cancelRef.current);

  // Prepare download - fetch and show review
  const handlePrepareDownload = async () => {
    if (!project) return;

    cancelRef.current = false;
    setDownloadPhase('preparing');
    setIsLargeProject(false);
    setDownloadDialogOpen(true);
    setDownloadProgress({
      current: 0,
      total: 0,
      message: 'Preparing download preview...',
    });

    try {
      // Step 1: Fetch all deliverables for the project (project-level and program-level)
      setDownloadProgress({
        current: 0,
        total: 0,
        message: 'Fetching deliverables...',
      });
      const allDeliverables = [];

      // Fetch project-level deliverables. We do page 1 synchronously so we
      // can learn totalPages, then fire pages 2..N in parallel — turns
      // sequential ~5×N latency into ~2× the slowest page on a 200-deliverable
      // project. Also uses a Set for dedup so the O(n²) array.find scan from
      // the old code stops biting at scale.
      const delPageSize = 50;
      const seenDelIds = new Set();
      const pushUnique = (items) => {
        for (const item of items) {
          if (item?.deliverableId && !seenDelIds.has(item.deliverableId)) {
            allDeliverables.push(item);
            seenDelIds.add(item.deliverableId);
          }
        }
      };

      try {
        const first = await fetchDeliverables({
          projectId: project.projectId,
          page: 1,
          limit: delPageSize,
        }).unwrap();
        pushUnique(first.deliverables || []);
        const total = Number.isFinite(first.total)
          ? first.total
          : (first.deliverables || []).length;
        const ps = Number.isFinite(first.limit) ? first.limit : delPageSize;
        const totalPages = ps > 0 ? Math.ceil(total / ps) : 1;

        if (totalPages > 1) {
          const remaining = [];
          for (let p = 2; p <= totalPages; p += 1) remaining.push(p);
          await runConcurrent(remaining, 4, async (page) => {
            try {
              const data = await fetchDeliverables({
                projectId: project.projectId,
                page,
                limit: delPageSize,
              }).unwrap();
              pushUnique(data.deliverables || []);
            } catch (err) {
              console.error(
                `Failed to fetch deliverable page ${page}:`,
                err,
              );
            }
          });
        }
      } catch (err) {
        console.error('Failed to fetch first deliverable page:', err);
      }

      // Also fetch program-level deliverables (as shown in Library). Same
      // page-1-then-parallel-rest pattern; dedup via the shared Set.
      if (project.programId) {
        try {
          const first = await fetchDeliverables({
            programId: project.programId,
            page: 1,
            limit: delPageSize,
          }).unwrap();
          pushUnique(first.deliverables || []);
          const total = Number.isFinite(first.total)
            ? first.total
            : (first.deliverables || []).length;
          const ps = Number.isFinite(first.limit) ? first.limit : delPageSize;
          const totalPages = ps > 0 ? Math.ceil(total / ps) : 1;

          if (totalPages > 1) {
            const remaining = [];
            for (let p = 2; p <= totalPages; p += 1) remaining.push(p);
            await runConcurrent(remaining, 4, async (page) => {
              try {
                const data = await fetchDeliverables({
                  programId: project.programId,
                  page,
                  limit: delPageSize,
                }).unwrap();
                pushUnique(data.deliverables || []);
              } catch (err) {
                console.error(
                  `Failed to fetch program deliverable page ${page}:`,
                  err,
                );
              }
            });
          }
        } catch (err) {
          console.error('Failed to fetch first program deliverable page:', err);
        }
      }

      if (cancelRef.current) return;
      // Step 2: Fetch project-level attachments
      setDownloadProgress({
        current: 0,
        total: 0,
        message: 'Fetching project attachments...',
      });
      let projectAttachments = [];
      try {
        const response = await fetchAttachments({
          objectId: project.projectId,
          objectType: 'Project',
          pageSize: 100,
        }).unwrap();
        projectAttachments = response?.attachments || [];
        console.log('Project attachments found:', projectAttachments.length);
      } catch (error) {
        console.error('Error fetching project attachments:', error);
      }

      if (cancelRef.current) return;
      // Step 3: Fetch deliverable-level attachments
      const deliverableAttachmentsMap = new Map();
      const delTotal = allDeliverables.length;
      // Heuristic: 30+ deliverables means prep is going to take noticeable
      // wall time even with concurrency 6 (it's still ~5 batches of round
      // trips, plus the form-type cascades). Latch the hint so users see a
      // reassuring message instead of wondering if the app froze.
      if (delTotal >= 30) {
        setIsLargeProject(true);
      }
      let delDone = 0;
      setDownloadProgress({
        current: 0,
        total: delTotal,
        message: `Checking deliverable attachments (0 of ${delTotal})...`,
      });

      console.log('Total deliverables found:', delTotal);
      console.log('Sample deliverable:', allDeliverables[0]);

      // Concurrency cap of 6 — empirically a good balance between throughput
      // and not stampeding the API gateway. Bump higher if BE handles it.
      await runConcurrent(allDeliverables, 6, async (deliverable) => {
        try {
          const response = await fetchAttachments({
            objectId: deliverable.deliverableId,
            objectType: 'Deliverable',
            pageSize: 100,
          }).unwrap();
          const attachments = response?.attachments || [];
          if (attachments.length > 0) {
            deliverableAttachmentsMap.set(deliverable.deliverableId, {
              deliverable,
              attachments,
            });
          }
        } catch (error) {
          console.error(
            `Error fetching attachments for deliverable ${deliverable.deliverableId}:`,
            error,
          );
        } finally {
          delDone += 1;
          setDownloadProgress({
            current: delDone,
            total: delTotal,
            message: `Checking deliverable attachments (${delDone} of ${delTotal})...`,
          });
        }
      });

      if (cancelRef.current) return;
      // Step 4: Fetch TAR (Travel Authorization Request) attachments
      const tarAttachmentsMap = new Map();
      let tarsCount = 0;
      if (isFormTypeAvailable(availableForms, 'tar')) {
        setDownloadProgress({
          current: 0,
          total: 0,
          message: 'Fetching TAR attachments...',
        });
        try {
          const tarResponse = await fetchTars({
            projectId: project.projectId,
            currentVersion: true,
            pageSize: 100,
          }).unwrap();

          const tars = tarResponse?.tars || [];
          tarsCount = tars.length;
          console.log('TARs found:', tars.length);

          let tarDone = 0;
          setDownloadProgress({
            current: 0,
            total: tars.length,
            message: `Checking TAR attachments (0 of ${tars.length})...`,
          });
          await runConcurrent(tars, 6, async (tar) => {
            try {
              const response = await fetchAttachments({
                objectId: tar.tarId,
                objectType: 'TAR',
                pageSize: 100,
              }).unwrap();
              const attachments = response?.attachments || [];
              if (attachments.length > 0) {
                tarAttachmentsMap.set(tar.tarId, { entity: tar, attachments });
              }
            } catch (error) {
              console.error(
                `Error fetching attachments for TAR ${tar.tarId}:`,
                error,
              );
            } finally {
              tarDone += 1;
              setDownloadProgress({
                current: tarDone,
                total: tars.length,
                message: `Checking TAR attachments (${tarDone} of ${tars.length})...`,
              });
            }
          });
        } catch (error) {
          console.error('Error fetching TARs:', error);
        }
      }

      if (cancelRef.current) return;
      // Step 6: Fetch TR (Trip Report) attachments
      const trAttachmentsMap = new Map();
      let tripReportsCount = 0;
      if (isFormTypeAvailable(availableForms, 'tr')) {
        setDownloadProgress({
          current: 0,
          total: 0,
          message: 'Fetching Trip Report attachments...',
        });
        try {
          const trResponse = await fetchTripReports({
            projectId: project.projectId,
            currentVersion: true,
            pageSize: 100,
          }).unwrap();

          const tripReports = trResponse?.items || [];
          tripReportsCount = tripReports.length;
          console.log('Trip Reports found:', tripReports.length);

          let trDone = 0;
          setDownloadProgress({
            current: 0,
            total: tripReports.length,
            message: `Checking Trip Report attachments (0 of ${tripReports.length})...`,
          });
          await runConcurrent(tripReports, 6, async (tr) => {
            try {
              const response = await fetchAttachments({
                objectId: tr.trId,
                objectType: 'TR',
                pageSize: 100,
              }).unwrap();
              const attachments = response?.attachments || [];
              if (attachments.length > 0) {
                trAttachmentsMap.set(tr.trId, { entity: tr, attachments });
              }
            } catch (error) {
              console.error(
                `Error fetching attachments for TR ${tr.trId}:`,
                error,
              );
            } finally {
              trDone += 1;
              setDownloadProgress({
                current: trDone,
                total: tripReports.length,
                message: `Checking Trip Report attachments (${trDone} of ${tripReports.length})...`,
              });
            }
          });
        } catch (error) {
          console.error('Error fetching Trip Reports:', error);
        }
      }

      if (cancelRef.current) return;
      // Step 7: Fetch PNR (Problem Notification Report) attachments
      const pnrAttachmentsMap = new Map();
      let pnrsCount = 0;
      if (isFormTypeAvailable(availableForms, 'pnr')) {
        setDownloadProgress({
          current: 0,
          total: 0,
          message: 'Fetching PNR attachments...',
        });
        try {
          const pnrResponse = await fetchPnrs({
            projectId: project.projectId,
            currentVersion: true,
            pageSize: 100,
          }).unwrap();

          const pnrs = pnrResponse?.pnrs || [];
          pnrsCount = pnrs.length;
          console.log('PNRs found:', pnrs.length);

          let pnrDone = 0;
          setDownloadProgress({
            current: 0,
            total: pnrs.length,
            message: `Checking PNR attachments (0 of ${pnrs.length})...`,
          });
          await runConcurrent(pnrs, 6, async (pnr) => {
            try {
              const response = await fetchAttachments({
                objectId: pnr.pnrId,
                objectType: 'PNR',
                pageSize: 100,
              }).unwrap();
              const attachments = response?.attachments || [];
              if (attachments.length > 0) {
                pnrAttachmentsMap.set(pnr.pnrId, { entity: pnr, attachments });
              }
            } catch (error) {
              console.error(
                `Error fetching attachments for PNR ${pnr.pnrId}:`,
                error,
              );
            } finally {
              pnrDone += 1;
              setDownloadProgress({
                current: pnrDone,
                total: pnrs.length,
                message: `Checking PNR attachments (${pnrDone} of ${pnrs.length})...`,
              });
            }
          });
        } catch (error) {
          console.error('Error fetching PNRs:', error);
        }
      }

      if (cancelRef.current) return;
      // Step 8: Fetch CTP (Consent to Purchase) attachments
      const ctpAttachmentsMap = new Map();
      let ctpsCount = 0;
      if (isFormTypeAvailable(availableForms, 'ctp')) {
        setDownloadProgress({
          current: 0,
          total: 0,
          message: 'Fetching CTP attachments...',
        });
        try {
          const ctpResponse = await fetchCtps({
            projectId: project.projectId,
            currentVersion: true,
            pageSize: 100,
          }).unwrap();

          const ctps = ctpResponse?.ctps || [];
          ctpsCount = ctps.length;
          console.log('CTPs found:', ctps.length);

          let ctpDone = 0;
          setDownloadProgress({
            current: 0,
            total: ctps.length,
            message: `Checking CTP attachments (0 of ${ctps.length})...`,
          });
          await runConcurrent(ctps, 6, async (ctp) => {
            try {
              const response = await fetchAttachments({
                objectId: ctp.ctpId,
                objectType: 'CTP',
                pageSize: 100,
              }).unwrap();
              const attachments = response?.attachments || [];
              if (attachments.length > 0) {
                ctpAttachmentsMap.set(ctp.ctpId, { entity: ctp, attachments });
              }
            } catch (error) {
              console.error(
                `Error fetching attachments for CTP ${ctp.ctpId}:`,
                error,
              );
            } finally {
              ctpDone += 1;
              setDownloadProgress({
                current: ctpDone,
                total: ctps.length,
                message: `Checking CTP attachments (${ctpDone} of ${ctps.length})...`,
              });
            }
          });
        } catch (error) {
          console.error('Error fetching CTPs:', error);
        }
      }

      if (cancelRef.current) return;
      // Step 9: Fetch RIP (Request to Initiate Purchase) attachments
      const ripAttachmentsMap = new Map();
      let ripsCount = 0;
      if (isFormTypeAvailable(availableForms, 'rip')) {
        setDownloadProgress({
          current: 0,
          total: 0,
          message: 'Fetching RIP attachments...',
        });
        try {
          const ripResponse = await fetchRips({
            projectId: project.projectId,
            currentVersion: true,
            pageSize: 100,
          }).unwrap();

          const rips = ripResponse?.rips || [];
          ripsCount = rips.length;
          console.log('RIPs found:', rips.length);

          let ripDone = 0;
          setDownloadProgress({
            current: 0,
            total: rips.length,
            message: `Checking RIP attachments (0 of ${rips.length})...`,
          });
          await runConcurrent(rips, 6, async (rip) => {
            try {
              const response = await fetchAttachments({
                objectId: rip.ripId,
                objectType: 'RIP',
                pageSize: 100,
              }).unwrap();
              const attachments = response?.attachments || [];
              if (attachments.length > 0) {
                ripAttachmentsMap.set(rip.ripId, { entity: rip, attachments });
              }
            } catch (error) {
              console.error(
                `Error fetching attachments for RIP ${rip.ripId}:`,
                error,
              );
            } finally {
              ripDone += 1;
              setDownloadProgress({
                current: ripDone,
                total: rips.length,
                message: `Checking RIP attachments (${ripDone} of ${rips.length})...`,
              });
            }
          });
        } catch (error) {
          console.error('Error fetching RIPs:', error);
        }
      }

      // Calculate total files to download
      const projectAttCount = projectAttachments.length;
      const deliverableAttCount = Array.from(
        deliverableAttachmentsMap.values(),
      ).reduce((sum, item) => sum + item.attachments.length, 0);
      const tarAttCount = Array.from(tarAttachmentsMap.values()).reduce(
        (sum, item) => sum + item.attachments.length,
        0,
      );
      const trAttCount = Array.from(trAttachmentsMap.values()).reduce(
        (sum, item) => sum + item.attachments.length,
        0,
      );
      const pnrAttCount = Array.from(pnrAttachmentsMap.values()).reduce(
        (sum, item) => sum + item.attachments.length,
        0,
      );
      const ctpAttCount = Array.from(ctpAttachmentsMap.values()).reduce(
        (sum, item) => sum + item.attachments.length,
        0,
      );
      const ripAttCount = Array.from(ripAttachmentsMap.values()).reduce(
        (sum, item) => sum + item.attachments.length,
        0,
      );
      const totalFiles =
        projectAttCount +
        deliverableAttCount +
        tarAttCount +
        trAttCount +
        pnrAttCount +
        ctpAttCount +
        ripAttCount;

      if (cancelRef.current) return;
      console.log('=== Download Summary ===');
      console.log('Project attachments:', projectAttCount);
      console.log(
        'Deliverables with attachments:',
        deliverableAttachmentsMap.size,
      );
      console.log('Total deliverable attachments:', deliverableAttCount);
      console.log('TARs:', tarsCount, '| Attachments:', tarAttCount);
      console.log(
        'Trip Reports:',
        tripReportsCount,
        '| Attachments:',
        trAttCount,
      );
      console.log('PNRs:', pnrsCount, '| Attachments:', pnrAttCount);
      console.log('CTPs:', ctpsCount, '| Attachments:', ctpAttCount);
      console.log('RIPs:', ripsCount, '| Attachments:', ripAttCount);
      console.log('>>> TOTAL FILES TO DOWNLOAD:', totalFiles);
      console.log('========================');

      if (totalFiles === 0) {
        setDownloadDialogOpen(false);
        // The dialog only needs the project name for its empty-state copy.
        // The full per-entity-type breakdown still lives in the console
        // log above for debugging.
        setDownloadSummary({
          projectName: project.name || project.code,
        });
        setNoAttachmentsDialogOpen(true);
        return;
      }

      // Store data for review and actual download
      setDownloadData({
        project,
        projectAttachments,
        deliverableAttachmentsMap,
        tarAttachmentsMap,
        trAttachmentsMap,
        pnrAttachmentsMap,
        ctpAttachmentsMap,
        ripAttachmentsMap,
        allDeliverables,
        tarsCount,
        tripReportsCount,
        pnrsCount,
        ctpsCount,
        ripsCount,
        totalFiles,
      });

      // Close preparation dialog and show review
      setDownloadDialogOpen(false);
      setDownloadReviewOpen(true);
    } catch (error) {
      console.error('Error preparing download:', error);
      setDownloadDialogOpen(false);
      setErrorMessage('Failed to prepare download. Please try again.');
      setDownloadErrorDialogOpen(true);
    }
  };

  // Execute download after review approval
  const handleExecuteDownload = async () => {
    if (!downloadData) return;

    const {
      project,
      projectAttachments,
      deliverableAttachmentsMap,
      tarAttachmentsMap,
      trAttachmentsMap,
      pnrAttachmentsMap,
      ctpAttachmentsMap,
      ripAttachmentsMap,
    } = downloadData;

    // Filter every attachment source through the user's selection so we
    // only fetch + ZIP what they actually want. We also recompute the
    // total file count from the filter so the progress bar reflects the
    // selected workload, not the full project.
    const includes = (a) => !!(a?.attachmentId && selectedIds.has(a.attachmentId));
    const filterAtts = (atts) => (atts || []).filter(includes);
    const totalFiles =
      filterAtts(projectAttachments).length +
      Array.from(deliverableAttachmentsMap?.values() || []).reduce(
        (s, r) => s + filterAtts(r.attachments).length,
        0,
      ) +
      [
        tarAttachmentsMap,
        trAttachmentsMap,
        pnrAttachmentsMap,
        ctpAttachmentsMap,
        ripAttachmentsMap,
      ].reduce(
        (s, m) =>
          s +
          Array.from(m?.values() || []).reduce(
            (s2, r) => s2 + filterAtts(r.attachments).length,
            0,
          ),
        0,
      );

    if (totalFiles === 0) return;

    // Close review and open progress
    cancelRef.current = false;
    downloadStartRef.current = Date.now();
    setDownloadPhase('downloading');
    setDownloadReviewOpen(false);
    setDownloadDialogOpen(true);
    setDownloadProgress({
      current: 0,
      total: 0,
      message: 'Starting download...',
    });

    try {
      const zip = new JSZip();
      const projectCode = project.code || 'Project';
      const rootFolder = zip.folder(`${projectCode}_Attachments`);

      // downloadedCount drives the visible "N of M files" progress and bumps
      // on every iteration regardless of fetch outcome — otherwise a stream
      // of failed fetches (auth blip, transient 5xx) leaves the counter
      // pinned at 1 forever even though we're working through the list.
      // successCount tracks files actually written to the ZIP so the final
      // summary can flag partial-failure runs honestly.
      let downloadedCount = 0;
      let successCount = 0;

      // Step 1: Download project attachments
      const projectFolder = rootFolder.folder('Project_Attachments');
      for (const attachment of projectAttachments) {
        if (cancelRef.current) break;
        if (!includes(attachment)) continue;
        try {
          setDownloadProgress({
            current: ++downloadedCount,
            total: totalFiles,
            message: `Downloading: ${attachment.name || 'file'}`,
          });

          const headers = {};
          if (loginToken) {
            headers['Authorization'] = `Bearer ${loginToken}`;
          }

          const response = await fetch(
            `/api/attachments/${attachment.attachmentId}/download`,
            {
              credentials: 'include',
              headers,
            },
          );

          if (response.ok) {
            const blob = await response.blob();
            const fileName =
              attachment.name || `attachment_${attachment.attachmentId}`;
            projectFolder.file(fileName, blob);
            successCount++;
          }
        } catch (error) {
          console.error(
            `Failed to download attachment ${attachment.attachmentId}:`,
            error,
          );
        }
      }

      // Step 2: Download deliverable attachments
      const deliverablesFolder = rootFolder.folder('Deliverables');
      for (const [
        deliverableId,
        { deliverable, attachments },
      ] of deliverableAttachmentsMap) {
        if (cancelRef.current) break;
        const deliverableType =
          deliverable.deliverableType?.abbreviation || 'DEL';
        const deliverableNumber =
          deliverable.deliverableNumber || deliverableId.slice(0, 4);
        const deliverableFolder = deliverablesFolder.folder(
          `${deliverableType}_${deliverableNumber}`,
        );

        for (const attachment of attachments) {
          if (cancelRef.current) break;
          if (!includes(attachment)) continue;
          try {
            setDownloadProgress({
              current: ++downloadedCount,
              total: totalFiles,
              message: `Downloading: ${attachment.name || 'file'}`,
            });

            const headers = {};
            if (loginToken) {
              headers['Authorization'] = `Bearer ${loginToken}`;
            }

            const response = await fetch(
              `/api/attachments/${attachment.attachmentId}/download`,
              {
                credentials: 'include',
                headers,
              },
            );

            if (response.ok) {
              const blob = await response.blob();
              const fileName =
                attachment.name || `attachment_${attachment.attachmentId}`;
              deliverableFolder.file(fileName, blob);
              successCount++;
            }
          } catch (error) {
            console.error(
              `Failed to download attachment ${attachment.attachmentId}:`,
              error,
            );
          }
        }
      }

      // Step 3: Download form attachments (TAR, TR, PNR, CTP, RIP)
      const formsFolder = rootFolder.folder('Forms');

      // Download TAR attachments
      if (tarAttachmentsMap && tarAttachmentsMap.size > 0) {
        const tarFolder = formsFolder.folder('TAR');
        for (const [tarId, { entity, attachments }] of tarAttachmentsMap) {
          if (cancelRef.current) break;
          const tarNumber = entity.tarNumber || tarId.slice(0, 8);
          const tarSubFolder = tarFolder.folder(`TAR_${tarNumber}`);

          for (const attachment of attachments) {
            if (cancelRef.current) break;
            if (!includes(attachment)) continue;
            try {
              setDownloadProgress({
                current: ++downloadedCount,
                total: totalFiles,
                message: `Downloading TAR: ${attachment.name || 'file'}`,
              });

              const headers = {};
              if (loginToken) {
                headers['Authorization'] = `Bearer ${loginToken}`;
              }

              const response = await fetch(
                `/api/attachments/${attachment.attachmentId}/download`,
                {
                  credentials: 'include',
                  headers,
                },
              );

              if (response.ok) {
                const blob = await response.blob();
                const fileName =
                  attachment.name || `attachment_${attachment.attachmentId}`;
                tarSubFolder.file(fileName, blob);
                successCount++;
              }
            } catch (error) {
              console.error(
                `Failed to download TAR attachment ${attachment.attachmentId}:`,
                error,
              );
            }
          }
        }
      }

      // Download TR (Trip Report) attachments
      if (trAttachmentsMap && trAttachmentsMap.size > 0) {
        const trFolder = formsFolder.folder('TR');
        for (const [trId, { entity, attachments }] of trAttachmentsMap) {
          if (cancelRef.current) break;
          const trNumber = entity.trNumber || trId.slice(0, 8);
          const trSubFolder = trFolder.folder(`TR_${trNumber}`);

          for (const attachment of attachments) {
            if (cancelRef.current) break;
            if (!includes(attachment)) continue;
            try {
              setDownloadProgress({
                current: ++downloadedCount,
                total: totalFiles,
                message: `Downloading TR: ${attachment.name || 'file'}`,
              });

              const headers = {};
              if (loginToken) {
                headers['Authorization'] = `Bearer ${loginToken}`;
              }

              const response = await fetch(
                `/api/attachments/${attachment.attachmentId}/download`,
                {
                  credentials: 'include',
                  headers,
                },
              );

              if (response.ok) {
                const blob = await response.blob();
                const fileName =
                  attachment.name || `attachment_${attachment.attachmentId}`;
                trSubFolder.file(fileName, blob);
                successCount++;
              }
            } catch (error) {
              console.error(
                `Failed to download TR attachment ${attachment.attachmentId}:`,
                error,
              );
            }
          }
        }
      }

      // Download PNR (Problem Notification Report) attachments
      if (pnrAttachmentsMap && pnrAttachmentsMap.size > 0) {
        const pnrFolder = formsFolder.folder('PNR');
        for (const [pnrId, { entity, attachments }] of pnrAttachmentsMap) {
          if (cancelRef.current) break;
          const pnrNumber = entity.pnrNumber || pnrId.slice(0, 8);
          const pnrSubFolder = pnrFolder.folder(`PNR_${pnrNumber}`);

          for (const attachment of attachments) {
            if (cancelRef.current) break;
            if (!includes(attachment)) continue;
            try {
              setDownloadProgress({
                current: ++downloadedCount,
                total: totalFiles,
                message: `Downloading PNR: ${attachment.name || 'file'}`,
              });

              const headers = {};
              if (loginToken) {
                headers['Authorization'] = `Bearer ${loginToken}`;
              }

              const response = await fetch(
                `/api/attachments/${attachment.attachmentId}/download`,
                {
                  credentials: 'include',
                  headers,
                },
              );

              if (response.ok) {
                const blob = await response.blob();
                const fileName =
                  attachment.name || `attachment_${attachment.attachmentId}`;
                pnrSubFolder.file(fileName, blob);
                successCount++;
              }
            } catch (error) {
              console.error(
                `Failed to download PNR attachment ${attachment.attachmentId}:`,
                error,
              );
            }
          }
        }
      }

      // Download CTP (Consent to Purchase) attachments
      if (ctpAttachmentsMap && ctpAttachmentsMap.size > 0) {
        const ctpFolder = formsFolder.folder('CTP');
        for (const [ctpId, { entity, attachments }] of ctpAttachmentsMap) {
          if (cancelRef.current) break;
          const ctpNumber = entity.ctpNumber || ctpId.slice(0, 8);
          const ctpSubFolder = ctpFolder.folder(`CTP_${ctpNumber}`);

          for (const attachment of attachments) {
            if (cancelRef.current) break;
            if (!includes(attachment)) continue;
            try {
              setDownloadProgress({
                current: ++downloadedCount,
                total: totalFiles,
                message: `Downloading CTP: ${attachment.name || 'file'}`,
              });

              const headers = {};
              if (loginToken) {
                headers['Authorization'] = `Bearer ${loginToken}`;
              }

              const response = await fetch(
                `/api/attachments/${attachment.attachmentId}/download`,
                {
                  credentials: 'include',
                  headers,
                },
              );

              if (response.ok) {
                const blob = await response.blob();
                const fileName =
                  attachment.name || `attachment_${attachment.attachmentId}`;
                ctpSubFolder.file(fileName, blob);
                successCount++;
              }
            } catch (error) {
              console.error(
                `Failed to download CTP attachment ${attachment.attachmentId}:`,
                error,
              );
            }
          }
        }
      }

      // Download RIP (Request to Initiate Purchase) attachments
      if (ripAttachmentsMap && ripAttachmentsMap.size > 0) {
        const ripFolder = formsFolder.folder('RIP');
        for (const [ripId, { entity, attachments }] of ripAttachmentsMap) {
          if (cancelRef.current) break;
          const ripNumber = entity.ripNumber || ripId.slice(0, 8);
          const ripSubFolder = ripFolder.folder(`RIP_${ripNumber}`);

          for (const attachment of attachments) {
            if (cancelRef.current) break;
            if (!includes(attachment)) continue;
            try {
              setDownloadProgress({
                current: ++downloadedCount,
                total: totalFiles,
                message: `Downloading RIP: ${attachment.name || 'file'}`,
              });

              const headers = {};
              if (loginToken) {
                headers['Authorization'] = `Bearer ${loginToken}`;
              }

              const response = await fetch(
                `/api/attachments/${attachment.attachmentId}/download`,
                {
                  credentials: 'include',
                  headers,
                },
              );

              if (response.ok) {
                const blob = await response.blob();
                const fileName =
                  attachment.name || `attachment_${attachment.attachmentId}`;
                ripSubFolder.file(fileName, blob);
                successCount++;
              }
            } catch (error) {
              console.error(
                `Failed to download RIP attachment ${attachment.attachmentId}:`,
                error,
              );
            }
          }
        }
      }

      // If the user hit Cancel mid-download, bail out without writing a
      // partial ZIP to disk. handleClose already cleared the dialog state.
      if (cancelRef.current) return;

      // Step 5: Generate and download ZIP
      setDownloadProgress({
        current: totalFiles,
        total: totalFiles,
        message: 'Creating ZIP file...',
      });

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `${projectCode}_Attachments.zip`);

      setDownloadProgress({
        current: totalFiles,
        total: totalFiles,
        message:
          successCount === totalFiles
            ? `Successfully downloaded all ${totalFiles} files!`
            : `Downloaded ${successCount} of ${totalFiles} files (${
                totalFiles - successCount
              } failed).`,
      });

      // Close dialog after short delay
      setTimeout(() => {
        setDownloadDialogOpen(false);
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error downloading project attachments:', error);
      setDownloadDialogOpen(false);
      setErrorMessage('Failed to download attachments. Please try again.');
      setDownloadErrorDialogOpen(true);
    }
  };

  const handleClose = () => {
    setDownloadReviewOpen(false);
    setDownloadDialogOpen(false);
    setNoAttachmentsDialogOpen(false);
    setDownloadErrorDialogOpen(false);
    setDownloadData(null);
    setDownloadSummary(null);
    setDownloadPhase('idle');
    setIsLargeProject(false);
    downloadStartRef.current = 0;
    onClose();
  };

  return (
    <>
      {/* Download Review Dialog */}
      {(() => {
        // Derived counts/sizes used by the review dialog. Hoisted so the JSX
        // stays scannable and we can hide empty sections without re-walking
        // the maps inline. Cheap to compute on each render — only runs when
        // the dialog is mounted and downloadData is set.
        // Helper: pluck a flat array of attachment IDs (skipping nullish) for
        // tri-state computation and bulk-toggle wiring.
        const idsFromAtts = (atts) =>
          (atts || []).map((a) => a?.attachmentId).filter(Boolean);

        const projectAtts = downloadData?.projectAttachments || [];
        const projectAttCount = projectAtts.length;
        const projectAttIds = idsFromAtts(projectAtts);

        const deliverableRows = Array.from(
          downloadData?.deliverableAttachmentsMap?.values() || [],
        );
        const deliverableAttCount = deliverableRows.reduce(
          (s, r) => s + r.attachments.length,
          0,
        );
        const deliverableAttIds = deliverableRows.flatMap((r) =>
          idsFromAtts(r.attachments),
        );

        // One entry per form type — only rendered when its attachment count > 0.
        // Forms with no attachments contribute nothing to the ZIP, so they have
        // no place in the "what you're about to download" review.
        const formGroups = [
          {
            key: 'tar',
            label: 'Travel Authorization Requests (TAR)',
            entityLabel: 'TAR',
            entityNumKey: 'tarNumber',
            map: downloadData?.tarAttachmentsMap,
          },
          {
            key: 'tr',
            label: 'Trip Reports (TR)',
            entityLabel: 'TR',
            entityNumKey: 'trNumber',
            map: downloadData?.trAttachmentsMap,
          },
          {
            key: 'pnr',
            label: 'Problem Notification Reports (PNR)',
            entityLabel: 'PNR',
            entityNumKey: 'pnrNumber',
            map: downloadData?.pnrAttachmentsMap,
          },
          {
            key: 'ctp',
            label: 'Consent to Purchase (CTP)',
            entityLabel: 'CTP',
            entityNumKey: 'ctpNumber',
            map: downloadData?.ctpAttachmentsMap,
          },
          {
            key: 'rip',
            label: 'Requests to Initiate Purchase (RIP)',
            entityLabel: 'RIP',
            entityNumKey: 'ripNumber',
            map: downloadData?.ripAttachmentsMap,
          },
        ].map((g) => {
          const rows = Array.from(g.map?.values() || []);
          const attCount = rows.reduce(
            (s, r) => s + r.attachments.length,
            0,
          );
          return { ...g, rows, attCount };
        });
        const formGroupsWithFiles = formGroups.filter((g) => g.attCount > 0);
        const formsTotalAtt = formGroupsWithFiles.reduce(
          (s, g) => s + g.attCount,
          0,
        );
        const formAttIds = formGroupsWithFiles.flatMap((g) =>
          g.rows.flatMap((r) => idsFromAtts(r.attachments)),
        );

        // Aggregated ID list across every section — used by the master
        // tri-state checkbox + the disabled state on the Download button.
        const allAttIds = [
          ...projectAttIds,
          ...deliverableAttIds,
          ...formAttIds,
        ];

        // Selected counts/sizes — these drive the summary line + button label.
        const selectedAtts = [];
        for (const a of projectAtts) {
          if (a?.attachmentId && selectedIds.has(a.attachmentId)) selectedAtts.push(a);
        }
        for (const { attachments } of deliverableRows) {
          for (const a of attachments) {
            if (a?.attachmentId && selectedIds.has(a.attachmentId)) selectedAtts.push(a);
          }
        }
        for (const g of formGroupsWithFiles) {
          for (const { attachments } of g.rows) {
            for (const a of attachments) {
              if (a?.attachmentId && selectedIds.has(a.attachmentId)) selectedAtts.push(a);
            }
          }
        }
        const selectedCount = selectedAtts.length;
        const totalFiles = downloadData?.totalFiles || 0;

        // Tri-states for the section + master checkboxes.
        const masterState = triState(allAttIds);
        const projectState = triState(projectAttIds);
        const deliverableState = triState(deliverableAttIds);
        const formState = triState(formAttIds);

        // Label text: "5 files" when all selected, "3 of 5 files" when partial.
        // Singular/plural off the SELECTED count when partial, off TOTAL when full.
        const summaryFileWord =
          (selectedCount === totalFiles ? totalFiles : selectedCount) === 1
            ? 'file'
            : 'files';
        const summaryHead =
          selectedCount === totalFiles
            ? `${totalFiles} ${summaryFileWord}`
            : `${selectedCount} of ${totalFiles} ${summaryFileWord}`;
        // The button always reflects the user's selection — if it's everything,
        // we keep the friendly "Download all"; otherwise we name the count.
        const buttonLabel =
          selectedCount === 0
            ? 'Download'
            : selectedCount === totalFiles
              ? 'Download all'
              : `Download ${selectedCount} ${summaryFileWord}`;

        return (
          <Dialog
            open={downloadReviewOpen}
            onClose={handleClose}
            aria-labelledby="download-review-title"
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle
              id="download-review-title"
              sx={{ pb: 1 }}
            >
              Download —{' '}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {downloadData?.project?.name || 'Project'}
              </Box>{' '}
              files
            </DialogTitle>
            <DialogContent>
              {/* Master select + compact summary. The summary's headline number
                  reflects the user's selection, so it updates live as they
                  check/uncheck. */}
              <Box
                sx={{ display: 'flex', alignItems: 'center', mb: 2, ml: -1 }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={masterState === 'all'}
                      indeterminate={masterState === 'some'}
                      onChange={(e) =>
                        setManySelected(allAttIds, e.target.checked)
                      }
                      inputProps={{ 'aria-label': 'Select all files' }}
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">
                      <Box
                        component="span"
                        sx={{ color: 'text.primary', fontWeight: 600 }}
                      >
                        {summaryHead}
                      </Box>
                    </Typography>
                  }
                />
              </Box>

              {/* Project-Level Attachments — only when there's something to
                  show. Collapsed by default; chevron toggles, checkbox selects. */}
              {projectAttCount > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: -1 }}>
                    <IconButton
                      size="small"
                      onClick={() => toggleExpanded('project')}
                      aria-label={
                        isExpanded('project')
                          ? 'Collapse project-level attachments'
                          : 'Expand project-level attachments'
                      }
                    >
                      {isExpanded('project') ? <ExpandMore /> : <ChevronRight />}
                    </IconButton>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={projectState === 'all'}
                          indeterminate={projectState === 'some'}
                          onChange={(e) =>
                            setManySelected(projectAttIds, e.target.checked)
                          }
                          inputProps={{
                            'aria-label':
                              'Select all project-level attachments',
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600 }}
                        >
                          Project-Level Attachments ({projectAttCount})
                        </Typography>
                      }
                    />
                  </Box>
                  {isExpanded('project') && (
                    <Box
                      sx={{
                        maxHeight: 200,
                        overflow: 'auto',
                        bgcolor: 'background.default',
                        p: 1,
                        borderRadius: 1,
                        ml: 4,
                        mt: 0.5,
                      }}
                    >
                      {downloadData.projectAttachments.map((att, idx) => {
                        const id = att.attachmentId;
                        return (
                          <FormControlLabel
                            key={id || idx}
                            sx={{ display: 'flex', ml: -1, py: 0.25 }}
                            control={
                              <Checkbox
                                size="small"
                                checked={id ? selectedIds.has(id) : false}
                                disabled={!id}
                                onChange={() => toggleId(id)}
                              />
                            }
                            label={
                              <Typography variant="body2">
                                {att.name || 'Unnamed file'}
                              </Typography>
                            }
                          />
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}

              {/* Deliverable Attachments — collapsible at the section level;
                  each deliverable inside is its own mini-accordion with its
                  own tri-state, so "everything in DEL 1" is one click. */}
              {deliverableAttCount > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: -1 }}>
                    <IconButton
                      size="small"
                      onClick={() => toggleExpanded('deliverable')}
                      aria-label={
                        isExpanded('deliverable')
                          ? 'Collapse deliverable attachments'
                          : 'Expand deliverable attachments'
                      }
                    >
                      {isExpanded('deliverable') ? (
                        <ExpandMore />
                      ) : (
                        <ChevronRight />
                      )}
                    </IconButton>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={deliverableState === 'all'}
                          indeterminate={deliverableState === 'some'}
                          onChange={(e) =>
                            setManySelected(
                              deliverableAttIds,
                              e.target.checked,
                            )
                          }
                          inputProps={{
                            'aria-label':
                              'Select all deliverable attachments',
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600 }}
                        >
                          Deliverable Attachments ({deliverableAttCount})
                        </Typography>
                      }
                    />
                  </Box>
                  {isExpanded('deliverable') && (
                    <Box
                      sx={{
                        maxHeight: 400,
                        overflow: 'auto',
                        bgcolor: 'background.default',
                        p: 1,
                        borderRadius: 1,
                        ml: 4,
                        mt: 0.5,
                      }}
                    >
                      {deliverableRows.map(
                        ({ deliverable, attachments }, idx) => {
                          const delKey = `del-${
                            deliverable.deliverableId || idx
                          }`;
                          const delAttIds = idsFromAtts(attachments);
                          const delState = triState(delAttIds);
                          const delExpanded = isExpanded(delKey);
                          const delLabel = `${
                            deliverable.deliverableType?.abbreviation || 'DEL'
                          } ${deliverable.deliverableNumber || idx + 1}${
                            deliverable.title ? ` — ${deliverable.title}` : ''
                          }`;
                          return (
                            <Box key={delKey} sx={{ mb: 0.5 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  ml: -1,
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => toggleExpanded(delKey)}
                                  aria-label={
                                    delExpanded
                                      ? `Collapse ${delLabel}`
                                      : `Expand ${delLabel}`
                                  }
                                >
                                  {delExpanded ? (
                                    <ExpandMore fontSize="small" />
                                  ) : (
                                    <ChevronRight fontSize="small" />
                                  )}
                                </IconButton>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={delState === 'all'}
                                      indeterminate={delState === 'some'}
                                      onChange={(e) =>
                                        setManySelected(
                                          delAttIds,
                                          e.target.checked,
                                        )
                                      }
                                      inputProps={{
                                        'aria-label': `Select all in ${delLabel}`,
                                      }}
                                    />
                                  }
                                  label={
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {delLabel}
                                    </Typography>
                                  }
                                />
                              </Box>
                              {delExpanded && (
                                <Box sx={{ ml: 4 }}>
                                  {attachments.map((att, attIdx) => {
                                    const id = att.attachmentId;
                                    return (
                                      <FormControlLabel
                                        key={id || attIdx}
                                        sx={{
                                          display: 'flex',
                                          ml: -1,
                                          py: 0.25,
                                        }}
                                        control={
                                          <Checkbox
                                            size="small"
                                            checked={
                                              id ? selectedIds.has(id) : false
                                            }
                                            disabled={!id}
                                            onChange={() => toggleId(id)}
                                          />
                                        }
                                        label={
                                          <Typography variant="body2">
                                            {att.name || 'Unnamed file'}
                                          </Typography>
                                        }
                                      />
                                    );
                                  })}
                                </Box>
                              )}
                            </Box>
                          );
                        },
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/* Form Attachments — section + per-form-type accordions, each
                  with its own tri-state. Form-entities (TAR 12345) stay as
                  plain labels under the form-type group; that level of
                  granularity didn't earn its keep against the extra click. */}
              {formsTotalAtt > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: -1 }}>
                    <IconButton
                      size="small"
                      onClick={() => toggleExpanded('forms')}
                      aria-label={
                        isExpanded('forms')
                          ? 'Collapse form attachments'
                          : 'Expand form attachments'
                      }
                    >
                      {isExpanded('forms') ? <ExpandMore /> : <ChevronRight />}
                    </IconButton>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={formState === 'all'}
                          indeterminate={formState === 'some'}
                          onChange={(e) =>
                            setManySelected(formAttIds, e.target.checked)
                          }
                          inputProps={{
                            'aria-label': 'Select all form attachments',
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600 }}
                        >
                          Form Attachments ({formsTotalAtt})
                        </Typography>
                      }
                    />
                  </Box>
                  {isExpanded('forms') && (
                    <Box
                      sx={{
                        bgcolor: 'background.default',
                        p: 1,
                        borderRadius: 1,
                        maxHeight: 400,
                        overflow: 'auto',
                        ml: 4,
                        mt: 0.5,
                      }}
                    >
                      {formGroupsWithFiles.map((g) => {
                        const gKey = `form-${g.key}`;
                        const gAttIds = g.rows.flatMap((r) =>
                          idsFromAtts(r.attachments),
                        );
                        const gState = triState(gAttIds);
                        const gExpanded = isExpanded(gKey);
                        return (
                          <Box key={gKey} sx={{ mb: 0.5 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                ml: -1,
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => toggleExpanded(gKey)}
                                aria-label={
                                  gExpanded
                                    ? `Collapse ${g.label}`
                                    : `Expand ${g.label}`
                                }
                              >
                                {gExpanded ? (
                                  <ExpandMore fontSize="small" />
                                ) : (
                                  <ChevronRight fontSize="small" />
                                )}
                              </IconButton>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={gState === 'all'}
                                    indeterminate={gState === 'some'}
                                    onChange={(e) =>
                                      setManySelected(
                                        gAttIds,
                                        e.target.checked,
                                      )
                                    }
                                    inputProps={{
                                      'aria-label': `Select all ${g.label}`,
                                    }}
                                  />
                                }
                                label={
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {g.label} ({g.attCount})
                                  </Typography>
                                }
                              />
                            </Box>
                            {gExpanded && (
                              <Box sx={{ ml: 4 }}>
                                {g.rows.map(({ entity, attachments }, idx) => (
                                  <Box key={idx} sx={{ mt: 0.5 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 600, mb: 0.25 }}
                                    >
                                      {g.entityLabel}{' '}
                                      {entity[g.entityNumKey] || idx + 1}
                                    </Typography>
                                    {attachments.map((att, attIdx) => {
                                      const id = att.attachmentId;
                                      return (
                                        <FormControlLabel
                                          key={id || attIdx}
                                          sx={{
                                            display: 'flex',
                                            ml: 1,
                                            py: 0.25,
                                          }}
                                          control={
                                            <Checkbox
                                              size="small"
                                              checked={
                                                id
                                                  ? selectedIds.has(id)
                                                  : false
                                              }
                                              disabled={!id}
                                              onChange={() => toggleId(id)}
                                            />
                                          }
                                          label={
                                            <Typography variant="body2">
                                              {att.name || 'Unnamed file'}
                                            </Typography>
                                          }
                                        />
                                      );
                                    })}
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>Cancel</Button>
              <Button
                onClick={handleExecuteDownload}
                variant="contained"
                color="primary"
                autoFocus
                disabled={selectedCount === 0}
              >
                {buttonLabel}
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* Download Progress Dialog */}
      <Dialog
        open={downloadDialogOpen}
        onClose={() => {}}
        aria-labelledby="download-dialog-title"
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="download-dialog-title">
          {downloadPhase === 'downloading'
            ? 'Downloading files'
            : 'Preparing your download'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ width: '100%', mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {downloadProgress.message}
            </Typography>
            {downloadProgress.total > 0 && (
              <>
                <LinearProgress
                  variant="determinate"
                  value={
                    (downloadProgress.current / downloadProgress.total) * 100
                  }
                  sx={{ mb: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {downloadProgress.current} of {downloadProgress.total} files
                </Typography>
                {/* Rolling ETA. We only render once a few files are done
                    (downloadedCount ≥ 3) so the first noisy samples don't
                    flash a wildly-wrong number, and we drop it once we've
                    reached the total — at that point we're in the "Creating
                    ZIP" cleanup, not waiting on more files. */}
                {downloadPhase === 'downloading' &&
                  downloadStartRef.current > 0 &&
                  downloadProgress.current >= 3 &&
                  downloadProgress.current < downloadProgress.total &&
                  (() => {
                    const elapsed =
                      (Date.now() - downloadStartRef.current) / 1000;
                    if (elapsed <= 0) return null;
                    const avg = elapsed / downloadProgress.current;
                    const remaining =
                      downloadProgress.total - downloadProgress.current;
                    const etaSecs = Math.max(1, Math.round(remaining * avg));
                    let label;
                    if (etaSecs < 5) {
                      label = 'Almost done…';
                    } else if (etaSecs < 60) {
                      label = `About ${etaSecs} seconds remaining`;
                    } else {
                      const mins = Math.round(etaSecs / 60);
                      label = `About ${mins} minute${mins === 1 ? '' : 's'} remaining`;
                    }
                    return (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.25 }}
                      >
                        {label}
                      </Typography>
                    );
                  })()}
              </>
            )}
            {downloadProgress.total === 0 && <LinearProgress />}
            {downloadPhase === 'preparing' && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 1.5,
                  color: 'text.secondary',
                  minHeight: '1.5em',
                }}
              >
                {PREP_TIPS[tipIndex]}
              </Typography>
            )}
            {isLargeProject && downloadPhase !== 'idle' && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 1.5,
                  fontStyle: 'italic',
                  color: 'text.secondary',
                }}
              >
                Heads up — this is a large project. Hang tight; the progress
                will keep moving as we work through everything.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {downloadProgress.current === downloadProgress.total &&
          downloadProgress.total > 0 &&
          downloadPhase === 'downloading' ? (
            <Button onClick={handleClose} variant="contained">
              Close
            </Button>
          ) : (
            <Button onClick={handleCancelDownload} color="inherit">
              Cancel
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* No Attachments Info Dialog — minimal empty-state. The detail
          breakdown that lived here previously (per-form-type counts) was
          debug-noise: it listed every form type even when the project had
          none, hardcoded the type list, and provided no info the user
          could act on. If "what did we check?" debugging is ever needed,
          the prepare-download flow still logs the full summary to the
          console. */}
      <Dialog
        open={noAttachmentsDialogOpen}
        onClose={handleClose}
        aria-labelledby="no-attachments-dialog-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="no-attachments-dialog-title">
          Nothing to download
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {downloadSummary?.projectName
              ? `"${downloadSummary.projectName}" doesn't`
              : "This project doesn't"}{' '}
            have any files attached to it, its deliverables, or its forms yet.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Download Error Dialog */}
      <Dialog
        open={downloadErrorDialogOpen}
        onClose={handleClose}
        aria-labelledby="error-dialog-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="error-dialog-title">Download Error</DialogTitle>
        <DialogContent>
          <DialogContentText>{errorMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

ProjectDownloader.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  project: PropTypes.object,
};
